import React, { useEffect, useState } from 'react';
import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { SYNC_PROGRESS_REOPEN_IDLE_MS, SYNC_PROGRESS_UI } from 'constants/constant';
import bridge from './bridge';
import { useNetwork } from './NetworkGate';
import './SyncProgressBar.scss';

// Single non-blocking card showing ALL offline sync activity with a per-category breakdown:
//  - tickets/forms from the RN sync engine's outbox (`sync.progress` broadcast + `sync.status` initial)
//  - offline drafts from the FE auto-sync sweep (redux: offline.draftSync)
// Both sources are native-only, so in a plain browser `sync.status` rejects, no `sync.progress` events
// arrive, and the draft sweep never runs → no active rows → this renders null (RN-only by construction).
// Rows for a category appear only while that category is actively syncing, so the card auto-hides when
// everything finishes (the one-time completion toasts handle the "done" moment).
// The card only displays progress: hiding it with × never touches the sync itself.
export default function SyncProgressBar() {
  const { online } = useNetwork();
  const [progress, setProgress] = useState(null); // tickets/forms outbox progress (from RN)
  const [dismissed, setDismissed] = useState(false);
  const draftSync = useSelector((s) => s?.offline?.draftSync);

  useEffect(() => {
    let active = true;
    // Initial paint (e.g. app opened while offline with already-queued items). Rejects in the browser.
    bridge
      .call('sync.status')
      .then((s) => { if (active && s && s.progress) setProgress(s.progress); })
      .catch(() => {});
    // Live updates as items queue (offline) and upload (on reconnect).
    const off = bridge.on('sync.progress', (p) => { if (active) setProgress(p); });
    return () => { active = false; off(); };
  }, []);

  const outboxActive = !!(progress && progress.pending > 0);
  const draftActive = !!(draftSync && draftSync.active && draftSync.total > 0);

  const rows = [];
  if (outboxActive) {
    const { tickets, forms, comments } = progress;
    if (tickets && tickets.total > 0) rows.push({ key: 'tickets', label: 'Tickets', done: tickets.done, total: tickets.total });
    if (forms && forms.total > 0) rows.push({ key: 'forms', label: 'Forms', done: forms.done, total: forms.total });
    if (comments && comments.total > 0) rows.push({ key: 'comments', label: 'Comments', done: comments.done, total: comments.total });
  }
  if (draftActive) {
    rows.push({ key: 'drafts', label: 'Drafts', done: draftSync.done, total: draftSync.total });
  }
  const syncing = online && rows.length > 0;

  // A hidden card stays hidden for the rest of this sync; once idle, the next sync shows it again.
  useEffect(() => {
    if (!dismissed || syncing) return undefined;
    const timer = setTimeout(() => setDismissed(false), SYNC_PROGRESS_REOPEN_IDLE_MS);
    return () => clearTimeout(timer);
  }, [dismissed, syncing]);

  // Show the card only while ACTIVELY syncing (online). When offline with pending work we render nothing
  // (no "Waiting to sync…" state) — it appears once the device is back online and sync is in progress.
  if (!syncing || dismissed) return null;

  return (
    <div className="unified-sync" role="status" aria-live="polite">
      <div className="unified-sync__header">
        <SyncOutlined spin className="unified-sync__icon" />
        <span className="unified-sync__title">{SYNC_PROGRESS_UI.title}</span>
        <button
          type="button"
          className="unified-sync__close"
          aria-label={SYNC_PROGRESS_UI.close}
          title={SYNC_PROGRESS_UI.close}
          onClick={() => setDismissed(true)}
        >
          <CloseOutlined />
        </button>
      </div>

      {rows.map((r) => {
        const percent = r.total ? Math.round((r.done / r.total) * 100) : 0;
        return (
          <div className="unified-sync__row" key={r.key}>
            <div className="unified-sync__row-head">
              <span className="unified-sync__label">{r.label}</span>
              <span className="unified-sync__count">{`${r.done}/${r.total}`}</span>
            </div>
            <div className="unified-sync__track">
              <div className="unified-sync__fill" style={{ width: `${percent}%` }} />
            </div>
            <div className="unified-sync__percent">{`${percent}%`}</div>
          </div>
        );
      })}
    </div>
  );
}
